import { navigationRef, NavigationService } from '@/navigations';
import { resetRootToPostAuthTarget } from '@/navigations/resolve-post-auth-target';
import { useOidcInteractionStore, useSessionStore, type UserRole } from '@/stores';
import React, { useEffect, type ComponentType } from 'react';
import { BackHandler, StyleSheet, View } from 'react-native';
import { useShallow } from 'zustand/shallow';

const redirectGateStyles = StyleSheet.create({
  flex: { flex: 1 },
});

/**
 * While redirecting away from a protected tree, rendering `null` leaves the native stack
 * with no React subtree; on Android the hardware back button can then finish the activity.
 * A full-screen placeholder plus consuming back presses avoids that race.
 */
function NavigationRedirectGate() {
  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => sub.remove();
  }, []);
  return <View style={redirectGateStyles.flex} />;
}

/**
 * Wraps a screen component to require authentication.
 * If the user is not authenticated: resets the root navigator to UnAuth (EnterEmail).
 *
 * @example
 * const HomeScreen = withAuth(function HomeScreen() {
 *   return <View>...</View>;
 * });
 *
 * @example
 * export const ProfileScreen = withAuth(ProfileScreenBase);
 */
export function withAuth<P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: {
    skipRedirect?: boolean;
  },
) {
  const skipRedirect = options?.skipRedirect ?? false;

  function WithAuthenScreen(props: P) {
    const isAuthenticated = useSessionStore(useShallow(state => state.isAuthenticated));
    const isSoftLoggedOut = useSessionStore(useShallow(state => state.isSoftLoggedOut));
    // Soft logout keeps the session warm but locks the app to the biometric login screen.
    const effectiveAuthed = isAuthenticated && !isSoftLoggedOut;

    useEffect(() => {
      if (effectiveAuthed || skipRedirect) return;

      // Soft logout routes to the quick re-auth screen; a real sign-out goes to Welcome.
      const target: 'LoginWithBiometrics' | 'Welcome' = isSoftLoggedOut
        ? 'LoginWithBiometrics'
        : 'Welcome';

      let cancelled = false;
      let retryId: ReturnType<typeof setTimeout> | undefined;

      const timeoutId = setTimeout(() => {
        if (cancelled) return;
        if (navigationRef.isReady()) {
          NavigationService.resetRoot('UnAuth', { screen: target });
          return;
        }
        retryId = setTimeout(() => {
          if (cancelled) return;
          if (navigationRef.isReady()) {
            NavigationService.resetRoot('UnAuth', { screen: target });
          }
        }, 100);
      }, 0);

      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
        if (retryId) clearTimeout(retryId);
      };
    }, [effectiveAuthed, isSoftLoggedOut]);

    if (!effectiveAuthed) {
      return <NavigationRedirectGate />;
    }

    return <WrappedComponent {...props} />;
  }

  WithAuthenScreen.displayName = `WithAuthen(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Screen'
  })`;

  return WithAuthenScreen;
}

/**
 * Wraps a screen component to require unauthenticated.
 * If the user is authenticated: resets the root navigator to Auth (home dashboard).
 *
 * @example
 * const HomeScreen = withUnAuth(function HomeScreen() {
 *   return <View>...</View>;
 * });
 *
 * @example
 * export const ProfileScreen = withUnAuth(ProfileScreenBase);
 */
export function withUnAuth<P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: {
    skipRedirect?: boolean;
  },
) {
  function WithUnAuthScreen(props: P) {
    const isAuthenticated = useSessionStore(useShallow(state => state.isAuthenticated));
    const isSoftLoggedOut = useSessionStore(useShallow(state => state.isSoftLoggedOut));
    // During an OIDC interaction (web `/sso-login/:uid`) the SsoLogin screen must stay
    // mounted after Firebase authenticates so it can hand the ID token to the IdP — do NOT
    // reset to the dashboard while it owns the flow.
    const isOidcInteraction = useOidcInteractionStore(useShallow(state => state.active));
    // While soft logged out we stay in the UnAuth tree (biometric login) instead of bouncing
    // back into the app, even though the Firebase session is still valid.
    const effectiveAuthed = isAuthenticated && !isSoftLoggedOut;

    useEffect(() => {
      if (effectiveAuthed && !options?.skipRedirect && !isOidcInteraction) {
        if (navigationRef.isReady()) {
          // Route to the correct flow based on resolved role(s): RoleSelect / Auth / Guardian.
          resetRootToPostAuthTarget();
          return;
        }
      }
    }, [effectiveAuthed, isOidcInteraction]);

    if (effectiveAuthed && !isOidcInteraction) {
      return null;
    }

    return <WrappedComponent {...props} />;
  }

  WithUnAuthScreen.displayName = `WithUnAuth(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Screen'
  })`;

  return WithUnAuthScreen;
}

/**
 * Wraps a screen component to require authentication AND a specific active role.
 *
 * - Not authenticated → resets to UnAuth (EnterEmail).
 * - Authenticated but `activeRole` !== `requiredRole` (or not yet chosen) → resets to
 *   the resolved post-auth target (RoleSelect for multi-role, else the matching flow).
 *
 * @example
 * <Stack.Screen name="Guardian" component={withRoleAccess(GuardianStack, 'guardian')} />
 */
export function withRoleAccess<P extends object>(
  WrappedComponent: ComponentType<P>,
  requiredRole: UserRole,
) {
  function WithRoleAccessScreen(props: P) {
    const isAuthenticated = useSessionStore(useShallow(state => state.isAuthenticated));
    const isSoftLoggedOut = useSessionStore(useShallow(state => state.isSoftLoggedOut));
    const activeRole = useSessionStore(useShallow(state => state.activeRole));
    const hasAccess = isAuthenticated && !isSoftLoggedOut && activeRole === requiredRole;

    useEffect(() => {
      if (hasAccess) return;
      if (!navigationRef.isReady()) return;

      // Soft logout locks to the biometric login screen (session kept warm).
      if (isSoftLoggedOut) {
        NavigationService.resetRoot('UnAuth', { screen: 'LoginWithBiometrics' });
        return;
      }
      if (!isAuthenticated) {
        NavigationService.resetRoot('UnAuth', { screen: 'Welcome' });
        return;
      }
      resetRootToPostAuthTarget();
    }, [hasAccess, isAuthenticated, isSoftLoggedOut]);

    if (!hasAccess) {
      return <NavigationRedirectGate />;
    }

    return <WrappedComponent {...props} />;
  }

  WithRoleAccessScreen.displayName = `WithRoleAccess(${requiredRole})(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Screen'
  })`;

  return WithRoleAccessScreen;
}
